/**
 * СЕКЦИЯ «СООБЩЕНИЯ» В СТРОКЕ ПОИСКА НАД СПИСКОМ ЧАТОВ.
 *
 * Постановка (Антон, 24.09 13:30: «поиск по контенту на вэбе тоже не
 * работает»): до этой правки поле поиска сверяло ТОЛЬКО имя комнаты и текст
 * ПОСЛЕДНЕГО сообщения (`matches()` в RoomList). По истории не искалось
 * ничего — функции просто не существовало.
 *
 * УСТРОЙСТВО:
 *  - список комнат по имени остаётся нетронутым и идёт СВЕРХУ (он работал);
 *  - найденные сообщения — ОТДЕЛЬНАЯ секция НИЖЕ, со своим заголовком;
 *  - каждая строка: комната · автор · дата, под ними фрагмент текста с
 *    подсветкой совпадения; клик ведёт в комнату К ЭТОМУ СООБЩЕНИЮ
 *    (#/room/<roomId>/<eventId>, обработка уже есть в App/RoomView);
 *  - состояние видно ВСЕГДА: ищу / ничего не найдено / ошибка + «Повторить».
 *    Молчаливый пустой экран запрещён — именно на него жалобы сегодня.
 *
 * Данные тянет `useMessageSearch` (debounce + отмена устаревших запросов),
 * сам поиск — серверный, `POST /_matrix/client/v3/search`.
 */
import type { CSSProperties } from "react";
import { useT, useTF, useLocale } from "../i18n";
import { useMatrixClient } from "../matrix/clientContext";
import { snippetAround, splitHighlights } from "../matrix/messageSearch";
import { useMessageSearch } from "./useMessageSearch";
import { s } from "./styles";
import { color, radius, space as sp } from "./theme";

const st: Record<string, CSSProperties> = {
  row: {
    display: "block",
    width: "100%",
    textAlign: "left",
    border: "none",
    background: "transparent",
    padding: `${sp.sm}px ${sp.md}px`,
    borderRadius: radius.md,
    cursor: "pointer",
    font: "inherit",
    color: color.text,
  },
  head: {
    display: "flex",
    alignItems: "baseline",
    gap: sp.sm,
    fontSize: 12,
    marginBottom: 2,
  },
  room: {
    fontWeight: 600,
    color: color.text,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "60%",
  },
  sender: {
    color: color.textDim,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  date: {
    marginLeft: "auto",
    flex: "0 0 auto",
    color: color.textMuted,
    fontSize: 11,
  },
  body: {
    fontSize: 13,
    lineHeight: 1.4,
    color: color.textDim,
    // Фрагмент в ДВЕ строки: одна строка режет слишком много контекста,
    // а строка чата выше и так фиксирована по высоте — здесь она другая.
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  mark: {
    background: color.highlight,
    color: color.text,
    borderRadius: 2,
    padding: "0 1px",
  },
  state: {
    padding: `${sp.md}px ${sp.md}px`,
    fontSize: 13,
    color: color.textDim,
    lineHeight: 1.5,
  },
  errorBox: {
    padding: `${sp.md}px`,
    margin: `${sp.sm}px ${sp.md}px`,
    borderRadius: radius.md,
    background: color.dangerBg,
    color: color.danger,
    fontSize: 13,
    lineHeight: 1.5,
  },
  moreButton: {
    display: "block",
    width: `calc(100% - ${sp.md * 2}px)`,
    margin: `${sp.sm}px ${sp.md}px`,
    padding: `${sp.sm}px`,
    borderRadius: radius.md,
    border: `1px solid ${color.border}`,
    background: color.bg,
    color: color.text,
    font: "inherit",
    fontSize: 13,
    cursor: "pointer",
  },
};

export function MessageSearchResults({
  query,
  onOpen,
}: {
  /** Сырая строка из поля поиска. Обрезку и минимальную длину решает хук. */
  query: string;
  /** Переход в комнату к конкретному сообщению. */
  onOpen: (roomId: string, eventId: string) => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const locale = useLocale();
  const client = useMatrixClient();
  const search = useMessageSearch(client, query);

  // Пустая строка поиска — секции нет вообще: ни заголовка, ни подсказок.
  if (search.status === "idle") return null;

  const dateLabel = (ts: number) =>
    new Date(ts).toLocaleString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });

  return (
    <div data-testid="message-search">
      <div style={s.sectionDivider} />
      <div style={s.sectionLabel} data-testid="message-search-label">
        {search.count !== undefined && search.hits.length > 0
          ? tfmt("search.messagesCount", { count: search.count })
          : t("search.messagesSection")}
      </div>

      {/* «Слишком короткий запрос» — это тоже видимое состояние, а не тишина:
          иначе человек печатает одну букву и не понимает, ищет клиент или нет. */}
      {search.status === "short" && (
        <div style={st.state} data-testid="message-search-short">{t("search.tooShort")}</div>
      )}

      {search.status === "loading" && (
        <div style={st.state} data-testid="message-search-loading">{t("search.searching")}</div>
      )}

      {search.status === "error" && (
        <div style={st.errorBox} data-testid="message-search-error" role="alert">
          <div>{tfmt("search.error", { error: search.error ?? "" })}</div>
          <button
            type="button"
            style={{ ...st.moreButton, margin: `${sp.sm}px 0 0`, width: "auto", padding: `4px 10px` }}
            onClick={search.retry}
            data-testid="message-search-retry"
          >{t("search.retry")}</button>
        </div>
      )}

      {search.status === "ready" && search.hits.length === 0 && (
        <div style={st.state} data-testid="message-search-empty">
          {tfmt("search.noMessages", { query: search.term })}
        </div>
      )}

      {search.hits.map((hit) => {
        const roomName = hit.roomName ?? t("search.unknownRoom");
        const snippet = snippetAround(hit.body, search.highlights);
        return (
          <button
            key={hit.eventId}
            type="button"
            className="alg-search-hit"
            style={st.row}
            data-testid="message-search-hit"
            data-room-id={hit.roomId}
            data-event-id={hit.eventId}
            aria-label={tfmt("search.resultAria", { sender: hit.senderName, room: roomName })}
            onClick={() => onOpen(hit.roomId, hit.eventId)}
          >
            <span style={st.head}>
              <span style={st.room}>{roomName}</span>
              <span style={st.sender}>{hit.senderName}</span>
              <span style={st.date}>{dateLabel(hit.ts)}</span>
            </span>
            <span style={st.body}>
              {splitHighlights(snippet, search.highlights).map((part, i) =>
                part.hit
                  ? <mark key={i} style={st.mark}>{part.text}</mark>
                  : <span key={i}>{part.text}</span>,
              )}
            </span>
          </button>
        );
      })}

      {/* Догрузка порциями. Кнопка, а не бесконечная прокрутка: список чатов
          и результаты живут в ОДНОМ скролле, и автозагрузка при докрутке
          мешала бы добраться до нижних комнат. Уже показанное не теряется. */}
      {search.hasMore && !search.loadingMore && (
        <button
          type="button"
          style={st.moreButton}
          onClick={search.loadMore}
          data-testid="message-search-more"
        >{t("search.more")}</button>
      )}
      {search.loadingMore && (
        <div style={st.state} data-testid="message-search-loading-more">{t("search.loadingMore")}</div>
      )}
    </div>
  );
}
